import React, { useEffect, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { request } from '../utils/request';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);

  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    username: '',
    password: '',
    first_name: '',
    last_name: '',
    email: '',
    mobile_number: '',
    address: '',
    department: '',
    location: '',
    roles: [],
  });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    const data = await request('users', { method: 'get' });
    setUsers(data);
  };

  const fetchRoles = async () => {
    const data = await request('roles', { method: 'get' });
    setRoles(data);
  };

  const handleOpen = (user = null) => {
    if (user) {
      setFormData({
        id: user._id,
        username: user.username,
        password: '',
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        mobile_number: user.mobile_number || '',
        address: user.address || '',
        department: user.department || '',
        location: user.location || '',
        roles: user.roles.map(r => r._id),
      });
      setIsEditing(true);
    } else {
      setFormData({
        id: null,
        username: '',
        password: '',
        first_name: '',
        last_name: '',
        email: '',
        mobile_number: '',
        address: '',
        department: '',
        location: '',
        roles: [],
      });
      setIsEditing(false);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const payload = { ...formData, roles: formData.roles };
    const opts = isEditing
      ? { method: 'put', params: formData.id, body: payload }
      : { method: 'post', body: payload };
    await request('users', opts);
    fetchUsers();
    handleClose();
  };

  return (
    <Box p={2}>
      {/* Header and Add Button */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.9rem' }}>
          <Link href="/home" underline="hover" color="inherit" sx={{ fontSize: '0.9rem' }}>
            Dashboard
          </Link>
          <Typography color="text.primary" sx={{ fontSize: '0.9rem' }}>
            Users
          </Typography>
        </Breadcrumbs>
        <Button
          variant="contained"
          size="small"
          sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, py: 0.5, px: 2 }}
          onClick={() => handleOpen()}
        >
          Add User
        </Button>
      </Stack>

      {/* Users Table */}
      <Paper elevation={1} sx={{ mb: 2 }}>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ backgroundColor: '#f0f7f5' }}>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u, idx) => (
                <TableRow key={u._id} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>{u.username}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.department}</TableCell>
                  <TableCell>
                    {u.roles?.map(r => r.name).join(', ') || '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => handleOpen(u)}
                      sx={{ color: '#00bf63', p: 0.5 }}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* User Form Dialog */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
     <DialogTitle
  sx={{
    backgroundColor: '#00bf63',
    color: '#fff',
    fontSize: '1rem',
    py: 1,
    textAlign: 'center' // This centers the text
  }}
>
  {isEditing ? 'Edit User' : 'Add User'}
</DialogTitle>

        <DialogContent sx={{ p: 2 , mt: '10px'  }}>
          <Stack spacing={1}>
            <TextField
              label="Username"
              name="username"
              value={formData.username}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="First Name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <Stack direction="row" spacing={1}>
              <TextField
                label="Mobile Number"
                name="mobile_number"
                value={formData.mobile_number}
                onChange={handleChange}
                fullWidth
                size="small"
              />
              <TextField
                label="Department"
                name="department"
                value={formData.department}
                onChange={handleChange}
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <TextField
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>Roles</InputLabel>
              <Select
                multiple
                name="roles"
                value={formData.roles}
                onChange={handleChange}
                input={<OutlinedInput label="Roles" />}
                renderValue={selected =>
                  roles
                    .filter(r => selected.includes(r._id))
                    .map(r => r.name)
                    .join(', ')
                }
              >
                {roles.map(r => (
                  <MenuItem key={r._id} value={r._id}>
                    <Checkbox checked={formData.roles.includes(r._id)} size="small" />
                    <ListItemText primary={r.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1, pr: 2 }}>
          <Button onClick={handleClose} sx={{ color: '#00bf63', fontSize: '0.875rem', px: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="small"
            sx={{ backgroundColor: '#00bf63', '&:hover': { backgroundColor: '#00794d' }, fontSize: '0.875rem', px: 2 }}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
